import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BulbOutlined, CheckOutlined, CloseOutlined, FireOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import { educationApi } from '../lib/wikiApi';
import { Button } from './ds';

/**
 * «Вопрос дня» (фаза 3.3, SM-2; вид HUB 2.0): одна просроченная карточка
 * повторения из сданных тестов. Варианты — крупные кнопки (на телефоне ≥44 px):
 * один ответ — сразу отправка, несколько — отметить и «Ответить».
 * autoOption — вариант из ссылки в TG/MAX (/qod?q=…&opt=…): отвечаем сразу.
 */
export function QuestionOfDay({ autoOption, compact }: { autoOption?: string | null; compact?: boolean }): JSX.Element | null {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const q = useQuery({ queryKey: ['wiki', 'review', 'today'], queryFn: () => educationApi.reviewToday(), retry: false, staleTime: 60_000 });
  const [picked, setPicked] = useState<string[]>([]);
  const [result, setResult] = useState<{ correct: boolean; explanation: string | null; correctOptionIds: string[]; nextInDays: number } | null>(null);
  const answer = useMutation({
    mutationFn: (ids: string[]) => educationApi.reviewAnswer(q.data!.question!.id, ids),
    onSuccess: async (r) => {
      setResult(r);
      message.open({ type: r.correct ? 'success' : 'info', content: r.correct ? 'Верно! Очки за повторение начислены' : 'Не совсем — посмотрите объяснение' });
      await qc.invalidateQueries({ queryKey: ['wiki', 'review'] });
      await qc.invalidateQueries({ queryKey: ['wiki', 'gamification', 'mine'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  useEffect(() => {
    if (autoOption && q.data?.question && !q.data.answered && !result && !answer.isPending && q.data.question.options.some((o) => o.id === autoOption)) {
      answer.mutate([autoOption]);
    }
  }, [autoOption, q.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (q.isLoading || q.error || !q.data) return null;
  const { question, answered, correct, dueCount, streakDays } = q.data;
  if (!question && !compact) return null;
  const multi = question?.kind === 'multi';
  const done = answered || !!result;
  const isRight = result?.correct ?? correct;

  const optState = (id: string): 'right' | 'wrong' | 'picked' | 'idle' => {
    if (result) {
      if (result.correctOptionIds.includes(id)) return 'right';
      if (picked.includes(id)) return 'wrong';
      return 'idle';
    }
    return picked.includes(id) ? 'picked' : 'idle';
  };

  return (
    <section className="fk-hub-card fk-hub-qod">
      <div className="fk-hub-card-head" style={{ marginBottom: 10 }}>
        <div className="fk-hub-card-title">
          <QuestionCircleOutlined />
          <h2>Вопрос дня</h2>
        </div>
        <span className="fk-hub-qod-meta">
          {streakDays > 0 && (
            <span title="Серия дней">
              <FireOutlined style={{ color: 'var(--fk-orange)' }} /> {streakDays}
            </span>
          )}
          <span>к повторению: {dueCount}</span>
        </span>
      </div>
      {!question ? (
        <div style={{ fontSize: 13.5, color: 'var(--fk-text-2)' }}>На сегодня всё повторили. Сдавайте тесты — вопросы вернутся по расписанию.</div>
      ) : (
        <>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 10, color: 'var(--fk-text)' }}>{question.text}</div>
          {done && !result ? (
            <div className="fk-hub-qod-result" data-ok={isRight ? 'true' : 'false'}>
              {isRight ? <CheckOutlined /> : <CloseOutlined />} {isRight ? 'Сегодня уже ответили верно' : 'Сегодня уже ответили — вопрос вернётся позже'}
            </div>
          ) : (
            <div className="fk-hub-qod-opts">
              {question.options.map((o) => {
                const st = optState(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    className="fk-hub-qod-opt"
                    data-state={st}
                    disabled={done || answer.isPending}
                    aria-pressed={picked.includes(o.id)}
                    onClick={() => {
                      if (multi) setPicked((p) => (p.includes(o.id) ? p.filter((x) => x !== o.id) : [...p, o.id]));
                      else {
                        setPicked([o.id]);
                        answer.mutate([o.id]);
                      }
                    }}
                  >
                    {multi && <span className="fk-hub-qod-box">{picked.includes(o.id) ? <CheckOutlined /> : null}</span>}
                    <span style={{ flex: 1 }}>{o.text}</span>
                    {st === 'right' && <CheckOutlined />}
                    {st === 'wrong' && <CloseOutlined />}
                  </button>
                );
              })}
            </div>
          )}
          {!done && multi && (
            <div style={{ marginTop: 10 }}>
              <Button size="sm" disabled={picked.length === 0} loading={answer.isPending} onClick={() => answer.mutate(picked)}>
                Ответить
              </Button>
            </div>
          )}
          {result && (
            <div className="fk-hub-qod-result" data-ok={result.correct ? 'true' : 'false'}>
              <b>{result.correct ? 'Верно!' : 'Неверно'}</b>
              {result.explanation && (
                <div style={{ marginTop: 4, color: 'var(--fk-text-2)' }}>
                  <BulbOutlined /> {result.explanation}
                </div>
              )}
              <div style={{ marginTop: 4, fontSize: 12, color: 'var(--fk-text-muted)' }}>Следующий показ через {result.nextInDays} дн.</div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
